<script setup lang="ts">
import { useCartStore } from '@/stores/cart'
import { useAuthStore } from '@/stores/auth'
import LanguageSwitcher from '@/components/LanguageSwitcher.vue'

const cartStore = useCartStore()
const authStore = useAuthStore()

// Public assets are served from /public and referenced by absolute path
const logoBrandUrl = '/logo-achiraya.svg'

defineEmits<{
  'open-login': []
}>()

async function handleLogout() {
  await authStore.logout()
}
</script>

<template>
  <header class="bg-white sticky top-0 z-50 border-b border-gray-100" data-testid="main-header">
    <div class="max-w-7xl mx-auto px-6 lg:px-8">
      <div class="flex justify-between items-center h-20">
        <!-- Logo -->
        <router-link to="/" class="flex items-center">
          <!-- Using URL constructor to properly reference public assets -->
          <img
            :src="logoBrandUrl"
            alt="Achiraya Studio"
            class="h-10 w-auto object-contain"
          />
        </router-link>

        <!-- Navigation -->
        <nav class="hidden md:flex items-center space-x-10">
          <router-link
            to="/"
            class="text-sm font-medium tracking-wide text-gray-900 hover:text-gray-600 transition-colors uppercase"
            active-class="text-gray-900"
          >
            {{ $t('nav.home') }}
          </router-link>
          <router-link
            to="/packages"
            class="text-sm font-medium tracking-wide text-gray-500 hover:text-gray-900 transition-colors uppercase"
            active-class="text-gray-900"
            data-testid="nav-packages"
          >
            {{ $t('nav.packages') }}
          </router-link>
          <router-link
            to="/booking"
            class="text-sm font-medium tracking-wide text-gray-500 hover:text-gray-900 transition-colors uppercase"
            active-class="text-gray-900"
            data-testid="nav-book-now"
          >
            {{ $t('nav.book_now') }}
          </router-link>
        </nav>

        <!-- Right Side: Language, Cart & Auth -->
        <div class="flex items-center space-x-4">
          <!-- Language Switcher -->
          <LanguageSwitcher />

          <!-- Cart -->
          <router-link
            to="/booking"
            class="relative p-2 text-gray-500 hover:text-gray-900 transition-colors"
            data-testid="cart-icon"
          >
            <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
            </svg>
            <span
              v-if="cartStore.itemCount > 0"
              class="absolute -top-0.5 -right-0.5 bg-gray-900 text-white text-[10px] font-medium rounded-full h-4 w-4 flex items-center justify-center"
              data-testid="cart-badge"
            >
              {{ cartStore.itemCount }}
            </span>
          </router-link>

          <!-- Login -->
          <button
            v-if="!authStore.isAuthenticated"
            @click="$emit('open-login')"
            class="text-sm font-medium text-gray-900 hover:text-gray-600 transition-colors"
            data-testid="login-button"
          >
            {{ $t('nav.login') }}
          </button>

          <!-- User Menu (when logged in) -->
          <div v-else class="flex items-center space-x-4" data-testid="user-menu">
            <span class="text-sm text-gray-600 hidden sm:inline" data-testid="user-name">{{ authStore.userName }}</span>
            <button
              @click="handleLogout"
              class="text-sm text-gray-500 hover:text-gray-900 transition-colors"
            >
              {{ $t('nav.logout') }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </header>
</template>
